import { Controller } from "@hotwired/stimulus";
import ClientSideValidations from "@client-side-validations/client-side-validations";

const INVALID_CLASSES = [
  "!border-red-300",
  "!bg-red-50/60",
  "hover:!border-red-400",
  "focus:!border-red-400",
  "focus:!bg-white",
  "focus:!ring-red-300/25",
];
const MESSAGE_CLASSES =
  "csv-message mt-2 flex items-start gap-1.5 text-xs font-medium text-red-600";
const TAB_PANEL = "[data-tabs-target='content']";
const WARNING_ICON = `<svg class="mt-px size-4 shrink-0 text-red-500" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M10.0297 3.65893C10.8859 2.1111 13.1111 2.11108 13.9673 3.65888L21.7131 17.6603C22.5427 19.16 21.4581 20.9995 19.7443 20.9995H4.25323C2.53945 20.9995 1.45483 19.16 2.28438 17.6604L10.0297 3.65893ZM12.9973 17.0009C12.9973 16.4494 12.5502 16.0022 11.9987 16.0022C11.4471 16.0022 11 16.4494 11 17.0009C11 17.5524 11.4471 17.9996 11.9987 17.9996C12.5502 17.9996 12.9973 17.5524 12.9973 17.0009ZM12.7381 9.14764C12.6882 8.7816 12.3742 8.4997 11.9945 8.5C11.5802 8.50033 11.2447 8.83639 11.2451 9.2506L11.2487 13.7522L11.2556 13.854C11.3055 14.22 11.6196 14.5019 11.9993 14.5016C12.4135 14.5013 12.749 14.1652 12.7487 13.751L12.7451 9.2494L12.7381 9.14764Z" fill="currentColor"/></svg>`;

const displayed = (element) => {
  const sibling = element.nextElementSibling;
  return element.tagName === "SELECT" && sibling?.classList.contains("ss-main")
    ? sibling
    : element;
};

const isHidden = (element) => element.getClientRects().length === 0;

const isNamedControl = (element) =>
  element.matches(
    "input:not([type=hidden],[type=submit],[type=button]), select, textarea",
  ) &&
  element.name &&
  !element.disabled;

const isReachable = (element) => {
  for (
    let node = displayed(element);
    node && node !== element.form;
    node = node.parentElement
  ) {
    if (!node.matches(TAB_PANEL) && getComputedStyle(node).display === "none")
      return false;
  }
  return true;
};

const concealedInputs = (form) =>
  [...form.elements].filter(
    (element) => isNamedControl(element) && isHidden(element),
  );

const NATIVE_MESSAGES = {
  valueMissing: () => "can't be blank",
  typeMismatch: () => "is invalid",
  patternMismatch: () => "is invalid",
  tooShort: (element) =>
    `is too short (minimum is ${element.minLength} characters)`,
  rangeUnderflow: (element) =>
    `must be greater than or equal to ${element.min}`,
  rangeOverflow: (element) => `must be less than or equal to ${element.max}`,
};

const nativeError = (element) => {
  if (element.validity.valid || isRemoved(element) || !isReachable(element))
    return;

  const state = Object.keys(NATIVE_MESSAGES).find(
    (key) => element.validity[key],
  );
  return state ? NATIVE_MESSAGES[state](element) : element.validationMessage;
};

export const showMessage = (form, key, message, anchor) => {
  let messageElement = form.querySelector(`.csv-message[data-for="${key}"]`);

  if (!messageElement) {
    messageElement = document.createElement("p");
    messageElement.className = MESSAGE_CLASSES;
    messageElement.dataset.for = key;
    messageElement.id = `${key}_error`;
    messageElement.setAttribute("aria-live", "polite");
    messageElement.innerHTML = `${WARNING_ICON}<span></span>`;
    anchor.after(messageElement);
  }

  messageElement.lastElementChild.textContent = message;
  return messageElement;
};

export const clearMessage = (form, key) =>
  form.querySelector(`.csv-message[data-for="${key}"]`)?.remove();

export const showError = (element, message, anchor = displayed(element)) => {
  const target = displayed(element);
  target.classList.add(...INVALID_CLASSES);
  target.setAttribute("aria-invalid", "true");

  const messageElement = showMessage(element.form, element.id, message, anchor);
  target.setAttribute("aria-describedby", messageElement.id);
};

export const clearError = (element) => {
  const target = displayed(element);
  target.classList.remove(...INVALID_CLASSES);
  target.removeAttribute("aria-invalid");
  target.removeAttribute("aria-describedby");
  clearMessage(element.form, element.id);
};

ClientSideValidations.formBuilders["ActionView::Helpers::FormBuilder"] = {
  add(element, _settings, message) {
    if (!element) return;

    const target = displayed(element);
    const wrapper = target.parentElement;
    const anchor =
      !wrapper || wrapper.contains(element.labels?.[0]) ? target : wrapper;
    showError(element, message, anchor);
  },

  remove(element) {
    if (element) clearError(element);
  },
};

const UNIQUENESS_URL = "/validators/uniqueness";
const NESTED_NAME = /^(.+_attributes\])\[[^\]]+\](.+)$/;

const siblingField = (element, attribute) =>
  element.form.elements.namedItem(
    element.name.replace(/\[[^\]]*\]$/, `[${attribute}]`),
  );

const isRemoved = (element) => siblingField(element, "_destroy")?.value === "1";

const duplicatedInForm = (element, options) => {
  const [, prefix, suffix] = element.name.match(NESTED_NAME) ?? [];
  if (!prefix) return false;

  const normalize = (value) =>
    options.case_sensitive ? value : value.toLowerCase();
  const value = normalize(element.value);
  return [...element.form.elements].some(
    (other) =>
      other.name !== element.name &&
      other.name?.startsWith(prefix) &&
      other.name.endsWith(suffix) &&
      !isRemoved(other) &&
      normalize(other.value) === value,
  );
};

const SCOPE_FIELDS = { ancestry: "parent_id" };

const scopeField = (element, key) =>
  [key, SCOPE_FIELDS[key]]
    .filter(Boolean)
    .map((attribute) => siblingField(element, attribute))
    .find((field) => field instanceof Element);

const revalidateOnChange = (field, element) => {
  field.csvScopeTargets ??= new Set();
  if (field.csvScopeTargets.has(element)) return;

  field.csvScopeTargets.add(element);
  field.addEventListener("change", () => {
    if (!element.value) return;
    element.dataset.csvChanged = "true";
    ClientSideValidations.isValid(element);
  });
};

const uniquenessParams = (element, options) => {
  const nameParts = element.name.match(/[^[\]]+/g) ?? [];
  const nested = /_attributes\]/.test(element.name);
  const klass = options.class ?? (nested ? null : nameParts[0]);
  if (!klass) return null;

  const params = new URLSearchParams({
    class: klass,
    attribute: nameParts.at(-1),
  });
  // Nested rows share one validator whose id is the last rendered row's
  const id = nested ? siblingField(element, "id")?.value : options.id;
  if (id) params.set("id", id);
  Object.entries(options.scope ?? {}).forEach(([key, value]) => {
    const field = scopeField(element, key);
    if (!field) return params.set(`scope[${key}]`, value ?? "");

    revalidateOnChange(field, element);
    params.set(`scope[${field.name.match(/[^[\]]+/g).at(-1)}]`, field.value);
  });

  return params;
};

ClientSideValidations.validators.remote.uniqueness = (element, options) => {
  if (element.value === "" && options.allow_blank) return;

  if (duplicatedInForm(element, options)) return options.message;

  const params = uniquenessParams(element, options);
  if (!params) return;

  // Server answers taken/not taken per value; cached per field and scope
  const key = params.toString();
  if (element.csvUniqueness?.key !== key)
    element.csvUniqueness = { key, results: new Map(), pending: new Map() };
  const { results, pending } = element.csvUniqueness;

  const value = element.value;
  if (results.has(value))
    return results.get(value) ? options.message : undefined;
  if (pending.has(value)) return;

  params.set("value", value);
  const request = fetch(UNIQUENESS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token":
        document.querySelector("meta[name='csrf-token']")?.content ?? "",
    },
    body: params,
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (!data) return;

      results.set(value, data.taken);
      if (element.value !== value) return;
      element.dataset.csvChanged = "true";
      ClientSideValidations.isValid(element);
    })
    .catch(() => {})
    .finally(() => pending.delete(value));

  pending.set(value, request);
};

const pendingUniquenessChecks = (form) =>
  [...form.elements].flatMap((element) => [
    ...(element.csvUniqueness?.pending.values() ?? []),
  ]);

const enableForm = ClientSideValidations.enablers.form;

ClientSideValidations.enablers.form = (form) => {
  enableForm(form);
  concealedInputs(form).forEach((input) =>
    ClientSideValidations.enablers.input(input),
  );
};

const validateConcealedInputs = (form) =>
  concealedInputs(form)
    .filter((element) => element.dataset.csvValidate && isReachable(element))
    .map((element) => ClientSideValidations.isValid(element))
    .every(Boolean);

const revealFirstInvalid = (form) => {
  const element = [...form.elements].find(
    (control) => control.dataset.csvValid === "false" && isReachable(control),
  );
  if (!element) return;

  element.dispatchEvent(new Event("invalid"));
  displayed(element).focus();
};

const formEvents = ClientSideValidations.eventsToBind.form;

ClientSideValidations.eventsToBind.form = (form) => {
  const events = formEvents(form);
  const validateOnSubmit = events.submit;

  events.submit = (event) => {
    const concealedValid = validateConcealedInputs(form);
    validateOnSubmit(event);
    if (!concealedValid && !event.defaultPrevented) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (event.defaultPrevented) return revealFirstInvalid(form);

    const pending = pendingUniquenessChecks(form);
    if (!pending.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.csvSubmitHeld) return;

    form.csvSubmitHeld = true;
    Promise.allSettled(pending).then(() => {
      form.csvSubmitHeld = false;
      if (form.isConnected) form.requestSubmit(event.submitter);
    });
  };

  return events;
};

// Connects to data-controller="client-side-validations"
export default class extends Controller {
  connect() {
    this.syncSubmitButtons = this.syncSubmitButtons.bind(this);
    this.validateSlimSelect = this.validateSlimSelect.bind(this);
    this.revalidateInvalid = this.revalidateInvalid.bind(this);
    this.applyNativeValidity = this.applyNativeValidity.bind(this);
    this.element.addEventListener(
      "element:validate:after",
      this.applyNativeValidity,
    );
    this.element.addEventListener(
      "element:validate:after",
      this.syncSubmitButtons,
    );
    this.element.addEventListener("change", this.validateSlimSelect, true);
    this.element.addEventListener("change", this.revalidateInvalid);
    ClientSideValidations.enable(this.element);

    this.observer = new MutationObserver((mutations) =>
      this.enableAddedInputs(mutations),
    );
    this.observer.observe(this.element, { childList: true, subtree: true });
  }

  disconnect() {
    this.observer.disconnect();
    this.element.removeEventListener(
      "element:validate:after",
      this.applyNativeValidity,
    );
    this.element.removeEventListener(
      "element:validate:after",
      this.syncSubmitButtons,
    );
    this.element.removeEventListener("change", this.validateSlimSelect, true);
    this.element.removeEventListener("change", this.revalidateInvalid);
  }

  enableAddedInputs(mutations) {
    mutations
      .flatMap((mutation) => [...mutation.addedNodes])
      .filter((node) => node instanceof Element)
      .flatMap((node) => [
        node,
        ...node.querySelectorAll("input, select, textarea"),
      ])
      .filter(isNamedControl)
      .forEach((input) => ClientSideValidations.enablers.input(input));
  }

  applyNativeValidity({ target }) {
    if (!target.validity || target.dataset.csvValid === "false") return;

    const message = nativeError(target);
    if (!message) return;

    target.dataset.csvValid = "false";
    this.element.ClientSideValidations.addError(target, message);
  }

  revalidateInvalid({ target }) {
    [...this.element.elements]
      .filter((control) => control !== target)
      .filter((control) => control.dataset.csvValid === "false")
      .forEach((control) => {
        control.dataset.csvChanged = "true";
        ClientSideValidations.isValid(control);
      });
  }

  validateSlimSelect({ target }) {
    if (!target.dataset.csvValidate || displayed(target) === target) return;

    target.dataset.csvChanged = "true";
    ClientSideValidations.isValid(target);
  }

  syncSubmitButtons() {
    const controls = [...this.element.elements];
    const invalid = controls.some(
      (control) => control.dataset.csvValid === "false",
    );

    controls
      .filter((control) => control.type === "submit")
      .forEach((button) =>
        invalid
          ? button.setAttribute("aria-disabled", "true")
          : button.removeAttribute("aria-disabled"),
      );
  }
}
